/**
 * React-Query hooks for the billing module.
 *
 *   - usePlans()              → GET  /api/v1/billing/plans
 *   - useStripeConfig()       → GET  /api/v1/billing/config
 *   - useSubscription()       → GET  /api/v1/billing/subscription
 *   - useInvoices()           → GET  /api/v1/billing/invoices
 *   - useCreateCheckoutIntent → POST /api/v1/billing/checkout/payment-intent
 *   - useFinalizeCheckout     → POST /api/v1/billing/checkout/finalize
 *   - useUpgradeNow           → POST /api/v1/billing/upgrade
 *
 * All sit behind the Flask auth gate (`@login_required`); `apiFetch`
 * attaches the Bearer JWT (and the session cookie in local dev). The
 * subscription query is the carrier of the trial-gate state the app
 * reads (`can_create_scene` / `can_create_video`), so mutations that
 * change subscription state invalidate it.
 */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';

import { apiFetch } from '@/lib/api-client';

export const PlanSchema = z.object({
  slug: z.string(),
  name: z.string(),
  blurb: z.string(),
  monthly_cents: z.number(),
  yearly_cents: z.number(),
  yearly_monthly_cents: z.number(),
  credits: z.number(),
  featured: z.boolean(),
  // Tier group: plans sharing a group (the Influencer credit tiers) render as
  // one card with a credit dropdown. null for a standalone plan.
  group: z.string().nullable().default(null),
});
export type Plan = z.infer<typeof PlanSchema>;

const PlansSchema = z.object({ plans: z.array(PlanSchema) });

const ConfigSchema = z.object({ publishable_key: z.string() });

export const CreditSummarySchema = z.object({
  monthly_included: z.number(),
  monthly_remaining: z.number(),
  period_end: z.string().nullable().optional(),
});
export type CreditSummary = z.infer<typeof CreditSummarySchema>;

export const EntitlementsSchema = z.object({
  max_active_avatars: z.number().nullable().optional(),
  active_avatar_count: z.number(),
  can_create_avatar: z.boolean(),
  video_models: z.array(z.string()).nullable().optional(),
  max_video_seconds: z.number().nullable().optional(),
  can_generate_video: z.boolean(),
  followed_accounts: z.boolean(),
  reel_analytics: z.boolean(),
});
export type Entitlements = z.infer<typeof EntitlementsSchema>;

export const SubscriptionStatusSchema = z.object({
  has_subscription: z.boolean(),
  plan_slug: z.string().nullable().optional(),
  interval: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  is_trialing: z.boolean(),
  trial_end: z.string().nullable().optional(),
  current_period_end: z.string().nullable().optional(),
  can_create_scene: z.boolean(),
  can_create_video: z.boolean(),
  credits: CreditSummarySchema.nullable().optional(),
  pending_plan_slug: z.string().nullable().optional(),
  pending_interval: z.string().nullable().optional(),
  pending_effective_at: z.string().nullable().optional(),
  entitlements: EntitlementsSchema.nullable().optional(),
});
export type SubscriptionStatus = z.infer<typeof SubscriptionStatusSchema>;

/**
 * Per-action credit charges for the on-button cost badges. Credit amounts only;
 * our internal provider USD cost is never sent to the client.
 */
export const CreditCostsSchema = z.object({
  image: z.number(),
  refine: z.number(),
  avatar_finalize: z.number(),
  voice: z.number(),
  voice_refine: z.number(),
  video_per_second: z.record(z.string(), z.number()),
});
export type CreditCosts = z.infer<typeof CreditCostsSchema>;

export const CheckoutIntentSchema = z.object({
  client_secret: z.string().nullable().optional(),
  intent_id: z.string(),
  // "payment" (the $1 card-validation PaymentIntent) or "setup" (a $0
  // SetupIntent when a promo code fully discounts the plan). Drives whether the
  // client calls confirmPayment or confirmSetup.
  mode: z.enum(['payment', 'setup']),
  amount: z.number(),
  currency: z.string(),
  plan_slug: z.string(),
  interval: z.string(),
  // Human-readable discount label (e.g. "100% off") when a promo code applied.
  discount_label: z.string().nullable().optional(),
  // Raw coupon shape so the summary can recompute the discounted monthly price.
  discount_percent_off: z.number().nullable().optional(),
  discount_amount_off: z.number().nullable().optional(),
});
export type CheckoutIntent = z.infer<typeof CheckoutIntentSchema>;

export const InvoiceSchema = z.object({
  stripe_invoice_id: z.string(),
  amount_paid: z.number(),
  currency: z.string(),
  status: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
  hosted_invoice_url: z.string().nullable().optional(),
  pdf_url: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
});
export type Invoice = z.infer<typeof InvoiceSchema>;

const InvoicesSchema = z.object({ invoices: z.array(InvoiceSchema) });

export const ChargeSchema = z.object({
  stripe_id: z.string(),
  amount: z.number(),
  currency: z.string(),
  kind: z.string(),
  status: z.string().nullable().optional(),
  reason: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
});
export type Charge = z.infer<typeof ChargeSchema>;

const ChargesSchema = z.object({ charges: z.array(ChargeSchema) });

/**
 * One credit spend-history row (what a generation cost). Credit amounts only;
 * our internal provider USD cost stays on the server.
 */
export const CreditLedgerEntrySchema = z.object({
  operation_kind: z.string(),
  operation_ref: z.string().nullable().optional(),
  status: z.string(),
  credits: z.number(),
  balance_after: z.number().nullable().optional(),
  created_at: z.string().nullable().optional(),
});
export type CreditLedgerEntry = z.infer<typeof CreditLedgerEntrySchema>;

const CreditHistorySchema = z.object({ history: z.array(CreditLedgerEntrySchema) });

export const PaymentMethodSchema = z.object({
  brand: z.string(),
  last4: z.string(),
  exp_month: z.number(),
  exp_year: z.number(),
});
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

const PaymentMethodEnvelopeSchema = z.object({
  payment_method: PaymentMethodSchema.nullable().optional(),
});

export const ChangePlanPreviewSchema = z.object({
  plan_slug: z.string(),
  plan_name: z.string(),
  interval: z.string(),
  amount_due_cents: z.number(),
  currency: z.string(),
  is_trial_giveup: z.boolean(),
  change_kind: z.enum(['upgrade', 'downgrade']),
  effective_at: z.string().nullable().optional(),
  payment_method: PaymentMethodSchema.nullable().optional(),
  // Credits added to the wallet the instant the switch is confirmed (full new
  // allotment on a trial give-up, the difference on a paid upgrade, 0 on a
  // deferred downgrade). Older servers omit it, so default to 0.
  credits_granted_now: z.number().default(0),
});
export type ChangePlanPreview = z.infer<typeof ChangePlanPreviewSchema>;

const SetupIntentSchema = z.object({
  setup_intent_id: z.string(),
  client_secret: z.string().nullable().optional(),
});
export type SetupIntent = z.infer<typeof SetupIntentSchema>;

export type BillingInterval = 'monthly' | 'yearly';

export const PLANS_KEY = ['billing', 'plans'] as const;
export const CONFIG_KEY = ['billing', 'config'] as const;
export const SUBSCRIPTION_KEY = ['billing', 'subscription'] as const;
export const INVOICES_KEY = ['billing', 'invoices'] as const;
export const CHARGES_KEY = ['billing', 'charges'] as const;
export const PAYMENT_METHOD_KEY = ['billing', 'payment-method'] as const;
export const CHANGE_PLAN_PREVIEW_KEY = ['billing', 'change-plan-preview'] as const;
// Cache-level identity for the checkout PaymentIntent mutation. Checkout reads
// its status/result via `useMutationState` filtered on this key rather than the
// observer returned by `useMutation`: the create-intent call fires from a mount
// effect behind a once-only ref, and in React StrictMode that combination
// detaches the observer from the mutation (TanStack/query#9068), wedging the
// observer on `pending` while the cache settles correctly.
export const CHECKOUT_INTENT_MUTATION_KEY = ['billing', 'checkout-intent'] as const;

export function usePlans(): UseQueryResult<Plan[], Error> {
  return useQuery({
    queryKey: PLANS_KEY,
    queryFn: async () => {
      const raw = await apiFetch<unknown>('/api/v1/billing/plans');
      return PlansSchema.parse(raw).plans;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useStripeConfig(): UseQueryResult<string, Error> {
  return useQuery({
    queryKey: CONFIG_KEY,
    queryFn: async () => {
      const raw = await apiFetch<unknown>('/api/v1/billing/config');
      return ConfigSchema.parse(raw).publishable_key;
    },
    staleTime: Infinity,
  });
}

export const CREDIT_COSTS_KEY = ['billing', 'credit-costs'] as const;

/** Per-action credit charges (image, refine, avatar_finalize, video/sec). */
export function useCreditCosts(): UseQueryResult<CreditCosts, Error> {
  return useQuery({
    queryKey: CREDIT_COSTS_KEY,
    queryFn: async () => {
      const raw = await apiFetch<unknown>('/api/v1/billing/credit-costs');
      return CreditCostsSchema.parse(raw);
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useSubscription(): UseQueryResult<SubscriptionStatus, Error> {
  return useQuery({
    queryKey: SUBSCRIPTION_KEY,
    queryFn: async () => {
      const raw = await apiFetch<unknown>('/api/v1/billing/subscription');
      return SubscriptionStatusSchema.parse(raw);
    },
    retry: false,
  });
}

/**
 * Convenience selector over `useSubscription` that returns the resolved
 * per-user entitlements (or null while loading / when absent). The plan-based
 * feature gates (avatar limit, video models/length, grow features) read this.
 */
export function useEntitlements(): Entitlements | null {
  const { data } = useSubscription();
  return data?.entitlements ?? null;
}

export function useInvoices(): UseQueryResult<Invoice[], Error> {
  return useQuery({
    queryKey: INVOICES_KEY,
    queryFn: async () => {
      const raw = await apiFetch<unknown>('/api/v1/billing/invoices');
      return InvoicesSchema.parse(raw).invoices;
    },
  });
}

export function useCharges(): UseQueryResult<Charge[], Error> {
  return useQuery({
    queryKey: CHARGES_KEY,
    queryFn: async () => {
      const raw = await apiFetch<unknown>('/api/v1/billing/charges');
      return ChargesSchema.parse(raw).charges;
    },
  });
}

export const CREDIT_HISTORY_KEY = ['billing', 'credit-history'] as const;

/** The user's credit spend ledger (what each generation cost), newest first. */
export function useCreditHistory(): UseQueryResult<CreditLedgerEntry[], Error> {
  return useQuery({
    queryKey: CREDIT_HISTORY_KEY,
    queryFn: async () => {
      const raw = await apiFetch<unknown>('/api/v1/billing/credit-history');
      return CreditHistorySchema.parse(raw).history;
    },
  });
}

export function useCreateCheckoutIntent() {
  return useMutation<
    CheckoutIntent,
    Error,
    { plan: string; interval: BillingInterval; promotionCode?: string | null }
  >({
    mutationKey: CHECKOUT_INTENT_MUTATION_KEY,
    mutationFn: async ({ plan, interval, promotionCode }) => {
      const raw = await apiFetch<unknown>('/api/v1/billing/checkout/payment-intent', {
        method: 'POST',
        body: { plan, interval, promotion_code: promotionCode ?? null },
      });
      return CheckoutIntentSchema.parse(raw);
    },
  });
}

export function useFinalizeCheckout() {
  const queryClient = useQueryClient();
  return useMutation<
    SubscriptionStatus,
    Error,
    {
      intentId: string;
      mode: 'payment' | 'setup';
      plan: string;
      interval: BillingInterval;
      promotionCode?: string | null;
    }
  >({
    mutationFn: async ({ intentId, mode, plan, interval, promotionCode }) => {
      const raw = await apiFetch<unknown>('/api/v1/billing/checkout/finalize', {
        method: 'POST',
        body: {
          intent_id: intentId,
          mode,
          plan,
          interval,
          promotion_code: promotionCode ?? null,
        },
      });
      return SubscriptionStatusSchema.parse(raw);
    },
    onSuccess: (status) => {
      queryClient.setQueryData(SUBSCRIPTION_KEY, status);
      void queryClient.invalidateQueries({ queryKey: INVOICES_KEY });
      void queryClient.invalidateQueries({ queryKey: CHARGES_KEY });
    },
  });
}

export function useChangePlan() {
  const queryClient = useQueryClient();
  return useMutation<SubscriptionStatus, Error, { plan: string; interval: BillingInterval }>({
    mutationFn: async ({ plan, interval }) => {
      const raw = await apiFetch<unknown>('/api/v1/billing/change-plan', {
        method: 'POST',
        body: { plan, interval },
      });
      return SubscriptionStatusSchema.parse(raw);
    },
    onSuccess: (status) => {
      queryClient.setQueryData(SUBSCRIPTION_KEY, status);
      void queryClient.invalidateQueries({ queryKey: INVOICES_KEY });
      void queryClient.invalidateQueries({ queryKey: CHARGES_KEY });
    },
  });
}

export function useCancelScheduledChange() {
  const queryClient = useQueryClient();
  return useMutation<SubscriptionStatus, Error, void>({
    mutationFn: async () => {
      const raw = await apiFetch<unknown>('/api/v1/billing/change-plan/cancel-scheduled', {
        method: 'POST',
      });
      return SubscriptionStatusSchema.parse(raw);
    },
    onSuccess: (status) => {
      queryClient.setQueryData(SUBSCRIPTION_KEY, status);
    },
  });
}

export function usePaymentMethod(): UseQueryResult<PaymentMethod | null, Error> {
  return useQuery({
    queryKey: PAYMENT_METHOD_KEY,
    queryFn: async () => {
      const raw = await apiFetch<unknown>('/api/v1/billing/payment-method');
      return PaymentMethodEnvelopeSchema.parse(raw).payment_method ?? null;
    },
  });
}

/**
 * Summarise a pending plan switch (amount, item, card on file) for the
 * confirmation modal. Disabled until a target plan/interval is chosen so the
 * preview only fires when the modal opens.
 */
export function useChangePlanPreview(
  target: { plan: string; interval: BillingInterval } | null,
): UseQueryResult<ChangePlanPreview, Error> {
  return useQuery({
    queryKey: [...CHANGE_PLAN_PREVIEW_KEY, target?.plan, target?.interval],
    enabled: target !== null,
    retry: false,
    queryFn: async () => {
      const params = new URLSearchParams({
        plan: target!.plan,
        interval: target!.interval,
      });
      const raw = await apiFetch<unknown>(
        `/api/v1/billing/change-plan/preview?${params.toString()}`,
      );
      return ChangePlanPreviewSchema.parse(raw);
    },
  });
}

export function useCreateSetupIntent() {
  return useMutation<SetupIntent, Error, void>({
    mutationFn: async () => {
      const raw = await apiFetch<unknown>('/api/v1/billing/setup-intent', { method: 'POST' });
      return SetupIntentSchema.parse(raw);
    },
  });
}

export function useSetPaymentMethod() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { paymentMethodId: string }>({
    mutationFn: async ({ paymentMethodId }) => {
      await apiFetch<unknown>('/api/v1/billing/payment-method', {
        method: 'POST',
        body: { payment_method_id: paymentMethodId },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PAYMENT_METHOD_KEY });
      void queryClient.invalidateQueries({ queryKey: CHANGE_PLAN_PREVIEW_KEY });
    },
  });
}

export function useUpgradeNow() {
  const queryClient = useQueryClient();
  return useMutation<SubscriptionStatus, Error, void>({
    mutationFn: async () => {
      const raw = await apiFetch<unknown>('/api/v1/billing/upgrade', { method: 'POST' });
      return SubscriptionStatusSchema.parse(raw);
    },
    onSuccess: (status) => {
      queryClient.setQueryData(SUBSCRIPTION_KEY, status);
      void queryClient.invalidateQueries({ queryKey: INVOICES_KEY });
      void queryClient.invalidateQueries({ queryKey: CHARGES_KEY });
    },
  });
}
